import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowUpRight } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api, formatCompactMoney } from "@/lib/api";
import { toast, Toaster } from "sonner";

const ERP_TONE = {
  Chempax: "bg-orange-100 text-brand-orange border-brand-orange/40",
  Syspro: "bg-blue-100 text-blue-700 border-blue-300",
  Infor: "bg-amber-100 text-amber-700 border-amber-300",
  Sage: "bg-emerald-100 text-emerald-700 border-emerald-300",
  QuickBooks: "bg-teal-100 text-teal-700 border-teal-300",
  Misys: "bg-pink-100 text-pink-700 border-pink-300",
  Smartsheet: "bg-yellow-100 text-yellow-700 border-yellow-300",
};

export default function CompaniesList() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/companies")
      .then((r) => setCompanies(r.data))
      .catch(() => toast.error("Failed to load companies"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Companies"
        subtitle={`${companies.length} entities across the TCP holding`}
      />
      <div className="p-6 animate-fade-in" data-testid="companies-list">
        {loading ? (
          <div className="text-sm text-slate-500">Loading companies…</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {companies.map((c) => (
              <button
                type="button"
                key={c.company_id}
                data-testid={`company-card-${c.company_id}`}
                onClick={() => navigate(`/companies/${c.company_id}`)}
                className="group flex flex-col rounded-md border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-navy-600 hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-navy-600 text-white">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-brand-orange" />
                </div>
                <div className="mt-4 font-display text-lg font-semibold text-navy-600">
                  {c.name}
                </div>
                <div className="text-xs text-slate-500">{c.industry} · {c.region}</div>
                <div className="mt-4 flex items-center gap-2">
                  <span
                    className={`rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                      ERP_TONE[c.erp_system] || "bg-slate-100 text-slate-600 border-slate-200"
                    }`}
                  >
                    {c.erp_system}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 tabular">
                    {c.currency}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
